import { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  chargeDraftProblems,
  chargePaymentProblems,
  chargeProblemStep,
  type ChargeDraft,
  type ChargeLimits,
  type ChargeStep,
  type PayToAccount,
  type Quote,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon, Modal, cn } from '@mypoz/ui';

import type { ChargeFields } from '@/features/china-payment/chargeFields.js';
import { FormField } from '@/features/china-payment/components/FormField.js';
import { QrDialog } from '@/features/china-payment/components/QrDialog.js';
import { ReceiptField } from '@/features/china-payment/components/ReceiptField.js';
import type { SendCharge } from '@/features/china-payment/useSendCharge.js';
import type { ReceiptSlot } from '@/features/orders/useReceiptUploads.js';
import { useCopyToClipboard } from '@/lib/clipboard.js';
import { useFormat } from '@/lib/format.js';
import { actionLinkClass, buttonClass, inputClass } from '@/lib/styles.js';
import { CHINA_PAYMENT_RECORDS_ROUTE } from '@/shell/navigation.js';

export interface PaymentStepProps {
  draft: ChargeDraft;
  limits: ChargeLimits;
  /** The quote the amount step settled on: the total to transfer. */
  quote: Quote;
  fields: ChargeFields;
  onChange: (patch: Partial<ChargeFields>) => void;
  accounts: readonly PayToAccount[];
  accountsStatus: 'loading' | 'ready' | 'error';
  onRetryAccounts: () => void;
  receipt: {
    readonly slot?: ReceiptSlot;
    readonly onPick: (file: File) => void;
    readonly onRetry: () => void;
    readonly onRemove: () => void;
  };
  onBack: () => void;
  /** Back to an earlier step whose answer no longer holds (nothing reachable leads there today). */
  onFix: (step: ChargeStep) => void;
  /**
   * Before the confirm dialog: makes sure the quote is recent, asking again when it is old. `changed`
   * means the total moved (the new one is now shown); `failed`, that no answer came.
   */
  ensureFreshQuote: () => Promise<'fresh' | 'changed' | 'failed'>;
  /** The card's sender: it outlives this step, so an unconfirmed attempt is never forgotten. */
  sender: SendCharge;
}

/**
 * Step 3 of a China Payment request (MYP-109), the old website's 付款资料: the RM total to transfer,
 * the MyPoz account paid into — its name, number to copy or QR — the receipt (required) and a
 * remark; then a confirm dialog and `ChargeResult`, once. A quote older than a few minutes is asked
 * again first (`ensureFreshQuote`): rates change daily, and the customer must transfer what staff
 * will expect.
 *
 * The old website also offers 上传二维码 (a QR picture, `qrcodefile`). It is left out until the
 * vendor confirms the API takes it.
 */
export function PaymentStep({
  draft,
  limits,
  quote,
  fields,
  onChange,
  accounts,
  accountsStatus,
  onRetryAccounts,
  receipt,
  onBack,
  onFix,
  ensureFreshQuote,
  sender,
}: PaymentStepProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const ids = { group: useId(), bankError: useId() };
  const form = useRef<HTMLDivElement>(null);
  const [tried, setTried] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [qrFor, setQrFor] = useState<PayToAccount | undefined>(undefined);
  const [quoteCheck, setQuoteCheck] = useState<'idle' | 'checking' | 'changed' | 'failed'>('idle');
  const { state, send, checkAgain } = sender;

  const problems = chargePaymentProblems(draft);
  const uploading = receipt.slot?.status === 'uploading';
  const sending = state === 'sending';
  const checking = state === 'checking';
  const account = accounts.find((item) => item.id === fields.bankId);
  const bankMissing = tried && problems.includes('bank');
  const type = draft.type;

  const submit = async () => {
    setTried(true);
    if (problems.length > 0 || uploading) {
      requestAnimationFrame(() =>
        form.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(),
      );
      return;
    }
    // Everything the earlier steps asked for must still hold, or the request could not be built.
    const earlier = chargeDraftProblems(draft, limits)[0];
    if (earlier !== undefined && chargeProblemStep(earlier) !== 'payment') {
      onFix(chargeProblemStep(earlier));
      return;
    }
    setQuoteCheck('checking');
    const fresh = await ensureFreshQuote();
    setQuoteCheck(fresh === 'fresh' ? 'idle' : fresh);
    if (fresh === 'fresh') setConfirming(true);
  };

  const confirm = async () => {
    // Resolves once `ChargeResult` has answered; a lost answer is then checked on the page.
    await send(draft, limits);
    setConfirming(false);
  };

  const confirmRows: [string, string][] = [
    [t('chinaPayment.form.confirmType'), t(`chinaPayment.types.${type}`)],
    [t('chinaPayment.request.amountLabel'), fmt.money(quote.rmbCents, 'RMB')],
    [t('chinaPayment.request.total'), fmt.money(quote.totalRmCents, 'RM')],
    [t('chinaPayment.form.paidTo'), account?.name ?? ''],
    ...(type === 'BANKTRANS'
      ? ([
          [t('chinaPayment.form.recipientName'), fields.recipientName.trim()],
          [t('chinaPayment.form.recipientCardNumber'), fields.recipientCardNumber.trim()],
        ] as [string, string][])
      : ([[t(`chinaPayment.form.payerAccount.${type}`), fields.payerAccount.trim()]] as [
          string,
          string,
        ][])),
  ];

  return (
    <div ref={form} className="grid gap-5">
      <div className="rounded-lg bg-surface-alt px-4 py-3">
        <div className="flex items-baseline justify-between gap-4">
          <p className="text-sm font-medium text-ink">{t('chinaPayment.form.transferAmount')}</p>
          <p className="text-lg font-semibold text-brand tabular-nums">
            {fmt.money(quote.totalRmCents, 'RM')}
          </p>
        </div>
        <p className="mt-1 text-xs text-muted">{t('chinaPayment.form.transferHint')}</p>
      </div>

      <fieldset aria-describedby={bankMissing ? ids.bankError : undefined}>
        <legend className="mb-1.5 text-sm text-muted">{t('chinaPayment.form.paidToChoose')}</legend>
        {accountsStatus === 'loading' ? (
          <p aria-busy="true" className="text-sm text-muted">
            {t('chinaPayment.payTo.loading')}
          </p>
        ) : accountsStatus === 'error' || accounts.length === 0 ? (
          <div className="grid justify-items-start gap-2">
            <p className="text-sm text-ink">{t('chinaPayment.form.paidToUnavailable')}</p>
            <button type="button" onClick={onRetryAccounts} className={buttonClass('secondary')}>
              <Icon name="refresh" size={18} />
              {t('actions.retry')}
            </button>
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {accounts.map((item, index) => (
              <label
                key={item.id}
                className={cn(
                  'flex min-h-11 cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2 text-sm',
                  'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand',
                  fields.bankId === item.id
                    ? 'border-brand bg-brand-soft'
                    : bankMissing
                      ? 'border-danger'
                      : 'border-line hover:bg-surface-alt',
                )}
              >
                <input
                  type="radio"
                  name={ids.group}
                  value={item.id}
                  checked={fields.bankId === item.id}
                  onChange={() => onChange({ bankId: item.id })}
                  aria-invalid={(index === 0 && bankMissing) || undefined}
                  className="accent-brand"
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-ink">{item.name}</span>
                  <span className="block truncate text-xs text-muted tabular-nums">
                    {item.accountNumber ??
                      (item.qrPath ? t('chinaPayment.form.qrOnly') : item.accountName)}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
        {bankMissing ? (
          <p id={ids.bankError} className="mt-1.5 text-xs text-danger">
            {t('chinaPayment.form.problem.bank')}
          </p>
        ) : null}
      </fieldset>

      {account ? <AccountDetails account={account} onShowQr={() => setQrFor(account)} /> : null}

      <ReceiptField
        {...(receipt.slot ? { slot: receipt.slot } : {})}
        onPick={receipt.onPick}
        onRetry={receipt.onRetry}
        onRemove={receipt.onRemove}
        missing={tried && problems.includes('receipt')}
      />

      <FormField
        label={
          type === 'ALIPAY' ? t('chinaPayment.form.remarkProxy') : t('chinaPayment.form.remark')
        }
      >
        {({ id }) => (
          <textarea
            id={id}
            rows={2}
            value={fields.remark}
            onChange={(event) => onChange({ remark: event.target.value })}
            className={inputClass('min-h-20 py-2')}
          />
        )}
      </FormField>

      {quoteCheck === 'changed' ? (
        <p role="alert" className="rounded-md bg-warning/10 p-3 text-sm text-ink">
          {t('chinaPayment.form.quoteChanged', { amount: fmt.money(quote.totalRmCents, 'RM') })}
        </p>
      ) : quoteCheck === 'failed' ? (
        <p role="alert" className="rounded-md bg-danger/10 p-3 text-sm text-danger">
          {t('chinaPayment.request.quoteFailed')}
        </p>
      ) : null}

      {state === 'refused' ? (
        <p role="alert" className="rounded-md bg-danger/10 p-3 text-sm text-danger">
          {t('chinaPayment.form.refused')}
        </p>
      ) : checking ? (
        <p
          role="status"
          aria-busy="true"
          className="flex items-center gap-2 rounded-md bg-surface-alt p-3 text-sm text-ink"
        >
          <Icon name="hourglass_empty" size={18} className="shrink-0 text-muted" />
          {t('chinaPayment.form.checking')}
        </p>
      ) : state === 'unconfirmed' ? (
        <div role="alert" className="grid gap-2 rounded-md bg-warning/10 p-3 text-sm text-ink">
          <p>{t('chinaPayment.form.unconfirmed')}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <button
              type="button"
              onClick={() => void checkAgain()}
              className={actionLinkClass('text-sm')}
            >
              <Icon name="refresh" size={16} />
              {t('chinaPayment.form.checkAgain')}
            </button>
            <Link to={CHINA_PAYMENT_RECORDS_ROUTE} className={actionLinkClass('text-sm')}>
              {t('chinaPayment.form.checkRecords')}
            </Link>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={sending || checking}
          className={buttonClass('secondary', 'px-5 disabled:opacity-60')}
        >
          <Icon name="arrow_back" size={18} />
          {t('chinaPayment.form.back')}
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={uploading || sending || checking || quoteCheck === 'checking'}
          className={buttonClass('primary', 'px-6 disabled:cursor-not-allowed disabled:opacity-60')}
        >
          {uploading ? t('chinaPayment.form.waitForReceipt') : t('chinaPayment.request.submit')}
        </button>
      </div>

      {confirming ? (
        <Modal
          title={t('chinaPayment.form.confirmTitle')}
          onClose={() => setConfirming(false)}
          dismissible={!sending}
          focusKey={sending ? 'sending' : 'idle'}
        >
          <dl className="grid gap-2 text-sm">
            {confirmRows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-4">
                <dt className="shrink-0 text-muted">{label}</dt>
                <dd className="min-w-0 text-right font-medium break-words text-ink tabular-nums">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs text-muted">{t('chinaPayment.request.staffConfirms')}</p>
          {state === 'unconfirmed' ? (
            <p className="mt-3 rounded-md bg-warning/10 p-3 text-sm text-ink">
              {t('chinaPayment.form.resendWarning')}
            </p>
          ) : null}
          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={sending}
              className={buttonClass('secondary')}
            >
              {t('actions.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={sending}
              aria-busy={sending || undefined}
              className={buttonClass('primary', 'disabled:opacity-60')}
            >
              {sending ? t('chinaPayment.form.sending') : t('chinaPayment.request.submit')}
            </button>
          </div>
        </Modal>
      ) : null}

      {qrFor?.qrPath ? (
        <QrDialog
          name={qrFor.name}
          {...(qrFor.accountName ? { accountName: qrFor.accountName } : {})}
          qrPath={qrFor.qrPath}
          onClose={() => setQrFor(undefined)}
        />
      ) : null}
    </div>
  );
}

/** The chosen account, as the old website shows it once picked: who it is, and how to pay it. */
function AccountDetails({ account, onShowQr }: { account: PayToAccount; onShowQr: () => void }) {
  const { t } = useTranslation();
  const { copied, failed, copy } = useCopyToClipboard();
  const copyLabel = failed
    ? t('chinaPayment.payTo.copyFailed')
    : copied
      ? t('chinaPayment.payTo.copied')
      : t('chinaPayment.payTo.copy');

  return (
    <dl className="relative grid gap-2 rounded-lg border border-line px-4 py-3 text-sm">
      {account.accountName ? (
        <div className="flex flex-wrap justify-between gap-x-4">
          <dt className="text-muted">{t('chinaPayment.form.accountHolder')}</dt>
          <dd className="font-medium text-ink">{account.accountName}</dd>
        </div>
      ) : null}
      {account.accountNumber ? (
        <div className="flex flex-wrap items-center justify-between gap-x-4">
          <dt className="text-muted">{t('chinaPayment.form.accountNumber')}</dt>
          <dd className="flex items-center gap-3">
            <span className="font-medium text-ink tabular-nums">{account.accountNumber}</span>
            <button
              type="button"
              onClick={() => void copy(account.accountNumber ?? '')}
              aria-label={`${copyLabel} — ${account.name}`}
              className={actionLinkClass(cn('text-sm', failed && 'text-danger'))}
            >
              {copyLabel}
              <Icon name={failed ? 'error' : copied ? 'check' : 'content_copy'} size={16} />
            </button>
            <span aria-live="polite" className="sr-only">
              {copied || failed ? `${account.name} ${copyLabel}` : ''}
            </span>
          </dd>
        </div>
      ) : null}
      {account.qrPath ? (
        <div className="flex flex-wrap items-center justify-between gap-x-4">
          <dt className="text-muted">{t('chinaPayment.form.payByQr')}</dt>
          <dd>
            <button
              type="button"
              onClick={onShowQr}
              aria-label={`${t('chinaPayment.payTo.showQr')} — ${account.name}`}
              className={actionLinkClass('text-sm')}
            >
              {t('chinaPayment.payTo.showQr')}
              <Icon name="qr_code_2" size={16} />
            </button>
          </dd>
        </div>
      ) : null}
    </dl>
  );
}
